import { useEffect, useRef, type KeyboardEvent } from 'react';

import { trapTabKey, useReturnFocusOnUnmount } from '../finance/dialog-focus';

/**
 * The modal contract the drawer and the «Оплачено» dialog share, built on the
 * finance desk's `dialog-focus.ts`: Tab wraps inside, Escape closes (not while
 * a write is in flight), the first field takes focus on open, and focus
 * returns to the trigger on close.
 */
export function useModal(onClose: () => void, busy: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  useReturnFocusOnUnmount();

  // Read through a ref: callers pass inline handlers, and an effect keyed on
  // them would re-run on every keystroke.
  const latest = useRef({ onClose, busy });
  latest.current = { onClose, busy };

  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape' && !latest.current.busy) latest.current.onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus();
  }, []);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => trapTabKey(ref.current, event);
  return { ref, onKeyDown };
}
